import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import AuditLogTooltip from './AuditLogTooltip';
import {
  AuditEventHourCountResponse,
  AuditEventInitiatorCountResponse,
  AuditEventIpCountResponse
} from 'api/adgang';

const SUCCESS_COLOR = '#009B96';
const FAILURE_COLOR = '#CA0000';
const HEATMAP_BASE = '0, 155, 150'; // teal (rgb), brukes med varierende alpha
const HEATMAP_EMPTY = '#F2F2F2';

// .NET DayOfWeek: 0=søndag ... 6=lørdag. Vi viser mandag først.
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_LABELS: Record<number, string> = {
  0: 'Søn',
  1: 'Man',
  2: 'Tir',
  3: 'Ons',
  4: 'Tor',
  5: 'Fre',
  6: 'Lør'
};

const num = (v: unknown): number => Number(v ?? 0);

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className='border rounded p-3 h-100'>
      <div className='fw-bold mb-2'>{title}</div>
      {children}
    </div>
  );
}

function Empty({ height }: { height: number }) {
  return (
    <div className='text-muted d-flex align-items-center justify-content-center' style={{ height }}>
      Ingen data
    </div>
  );
}

// Delt stablet søylediagram: Vellykket (teal) + Feilet (rød) per rad.
function StackedRankBar({
  data,
  height
}: {
  data: { name: string; Vellykket: number; Feilet: number }[];
  height: number;
}) {
  return (
    <ResponsiveContainer width='100%' height={height}>
      <BarChart data={data} layout='vertical' margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray='3 3' horizontal={false} />
        <XAxis type='number' allowDecimals={false} tick={{ fontSize: 12 }} />
        <YAxis type='category' dataKey='name' width={170} tick={{ fontSize: 11 }} interval={0} />
        <Tooltip content={<AuditLogTooltip />} />
        <Legend />
        <Bar dataKey='Vellykket' stackId='a' fill={SUCCESS_COLOR} />
        <Bar dataKey='Feilet' stackId='a' fill={FAILURE_COLOR} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function AuditLogSecurity({
  perIp,
  perInitiator,
  perHour
}: {
  perIp?: AuditEventIpCountResponse[];
  perInitiator?: AuditEventInitiatorCountResponse[];
  perHour?: AuditEventHourCountResponse[];
}) {
  const ipData = useMemo(
    () =>
      (perIp ?? []).map((x) => {
        const failed = num(x.failedCount);
        return { name: x.ip ?? 'Ukjent', Vellykket: Math.max(num(x.count) - failed, 0), Feilet: failed };
      }),
    [perIp]
  );

  const initiatorData = useMemo(
    () =>
      (perInitiator ?? []).map((x) => {
        const failed = num(x.failedCount);
        return {
          name: x.initiator ?? 'Ukjent',
          Vellykket: Math.max(num(x.count) - failed, 0),
          Feilet: failed
        };
      }),
    [perInitiator]
  );

  // 7x24-matrise + maks for fargeintensitet.
  const { matrix, maxCount } = useMemo(() => {
    const m: Record<number, Record<number, number>> = {};
    let max = 0;
    (perHour ?? []).forEach((h) => {
      const wd = num(h.weekday);
      const hr = num(h.hour);
      const c = num(h.count);
      if (!m[wd]) m[wd] = {};
      m[wd][hr] = (m[wd][hr] ?? 0) + c;
      if (m[wd][hr] > max) max = m[wd][hr];
    });
    return { matrix: m, maxCount: max };
  }, [perHour]);

  const cellColor = (count: number): string => {
    if (count <= 0 || maxCount <= 0) return HEATMAP_EMPTY;
    const alpha = 0.12 + 0.88 * Math.sqrt(count / maxCount);
    return `rgba(${HEATMAP_BASE}, ${alpha})`;
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hasHeat = (perHour ?? []).length > 0;

  return (
    <>
      <Row className='pb-3'>
        <Col sm={12} lg={6} className='mb-3'>
          <ChartCard title='Topp IP-adresser'>
            {ipData.length > 0 ? <StackedRankBar data={ipData} height={360} /> : <Empty height={360} />}
          </ChartCard>
        </Col>
        <Col sm={12} lg={6} className='mb-3'>
          <ChartCard title='Mest aktive aktører'>
            {initiatorData.length > 0 ? (
              <StackedRankBar data={initiatorData} height={360} />
            ) : (
              <Empty height={360} />
            )}
          </ChartCard>
        </Col>
      </Row>

      <Row className='pb-3'>
        <Col sm={12} className='mb-3'>
          <ChartCard title='Aktivitet etter ukedag og time'>
            {hasHeat ? (
              <div style={{ overflowX: 'auto' }}>
                <table className='text-center' style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
                  <thead>
                    <tr>
                      <th />
                      {hours.map((h) => (
                        <th key={h} style={{ fontSize: 10, fontWeight: 400, color: '#575757', width: 26 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKDAY_ORDER.map((wd) => (
                      <tr key={wd}>
                        <td style={{ fontSize: 11, paddingRight: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {WEEKDAY_LABELS[wd]}
                        </td>
                        {hours.map((h) => {
                          const count = matrix[wd]?.[h] ?? 0;
                          return (
                            <td
                              key={h}
                              title={`${WEEKDAY_LABELS[wd]} kl ${h}: ${count.toLocaleString('no-NO')}`}
                              style={{
                                width: 26,
                                height: 22,
                                borderRadius: 3,
                                backgroundColor: cellColor(count)
                              }}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty height={200} />
            )}
          </ChartCard>
        </Col>
      </Row>
    </>
  );
}

export default AuditLogSecurity;
